import React, { useEffect, useMemo, useRef, useState } from 'react';

// ---------------------------------------------------------------------------
// AnalyticsCharts — day-by-day overview for the admin analytics page.
//
// Plain SVG, no chart library, so it adds no dependency. Everything is derived
// from the same Redux slices the tables already read; nothing new is fetched.
// ---------------------------------------------------------------------------

const COLORS = {
  blue: '#007bff',
  lightBlue: '#9ec5fe',
  teal: '#1D9E75',
  purple: '#6f5fd0',
  amber: '#E0902B',
  coral: '#D85A30',
  slate: '#9aa0a6',
};
const SCREEN_COLORS = [COLORS.blue, COLORS.teal, COLORS.purple, COLORS.amber, COLORS.coral, COLORS.slate];
const BUCKET_ORDER = ['1', '2-3', '4-6', '7-10', '11-20', '21-40', '41+'];

// ---------------------------------------------------------------------------
// HELPERS
// ---------------------------------------------------------------------------
const unwrap = data => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.results)) return data.results;
  return [];
};
const num = v => Number(v) || 0;
const dayKey = d => String(d).slice(0, 10);
const isoLocal = d =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const parseIso = iso => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
};
const shortDay = iso => parseIso(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
const longDay = iso =>
  parseIso(iso).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });

const compactFmt = new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 });
const fmtInt = n => (n == null ? '—' : Math.round(n).toLocaleString());
const fmtCompact = n => (n == null ? '—' : compactFmt.format(n));
const fmtPct = v => (v == null ? '—' : `${(v * 100).toFixed(2)}%`);
const fmtPctAxis = v => `${+(v * 100).toFixed(1)}%`;
const fmtMin = v => (v == null ? '—' : `${v.toFixed(1)} min`);
const fmtMinAxis = v => `${+v.toFixed(1)}m`;

// Every calendar day from (today - windowDays) to today, plus any day that
// appears in the data. Days with no rollup row show as zero instead of
// silently disappearing from the x-axis.
const buildDays = (windowDays, maps) => {
  const set = new Set();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = new Date(today);
  start.setDate(start.getDate() - windowDays);
  for (const d = new Date(start); d <= today; d.setDate(d.getDate() + 1)) set.add(isoLocal(d));
  maps.forEach(m => Object.keys(m).forEach(k => set.add(k)));
  return [...set].sort();
};

// Axis with exactly k intervals so left and right gridlines line up.
const niceScale = (rawMax, k = 4, integer = true) => {
  const target = rawMax > 0 ? rawMax : 1;
  let pow = Math.pow(10, Math.floor(Math.log10(target / k)));
  if (integer) pow = Math.max(pow, 1);
  const step = [1, 2, 2.5, 5, 10]
    .map(f => f * pow)
    .filter(s => !integer || Number.isInteger(s))
    .find(s => s * k >= target * 0.999999);
  return {
    max: step * k,
    ticks: Array.from({ length: k + 1 }, (_, i) => +(i * step).toPrecision(12)),
  };
};

const useElementWidth = () => {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const update = () => setWidth(el.clientWidth);
    update();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
};

// ---------------------------------------------------------------------------
// LAYOUT PIECES
// ---------------------------------------------------------------------------
const SectionLabel = ({ children, first }) => (
  <div
    style={{
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      color: '#aaa',
      margin: `${first ? 8 : 26}px 0 10px`,
    }}
  >
    {children}
  </div>
);

const Card = ({ title, sub, children }) => (
  <div
    style={{
      border: '1px solid #dee2e6',
      borderRadius: 4,
      padding: '14px 16px 10px',
      background: '#fff',
      minWidth: 0,
    }}
  >
    <div style={{ fontSize: 14, fontWeight: 600, color: '#333' }}>{title}</div>
    {sub && <div style={{ fontSize: 11, color: '#888', marginTop: 2, marginBottom: 10 }}>{sub}</div>}
    {children}
  </div>
);

const Grid = ({ children }) => (
  <div
    style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(min(380px, 100%), 1fr))',
      gap: 16,
    }}
  >
    {children}
  </div>
);

const Kpi = ({ label, value, sub }) => (
  <div
    style={{
      flex: '1 1 150px',
      border: '1px solid #dee2e6',
      borderRadius: 4,
      padding: '10px 14px',
      background: '#fff',
    }}
  >
    <div style={{ fontSize: 12, color: '#888' }}>{label}</div>
    <div style={{ fontSize: 24, fontWeight: 600, color: '#333', fontVariantNumeric: 'tabular-nums' }}>
      {value}
    </div>
    {sub && <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{sub}</div>}
  </div>
);

const Placeholder = ({ height, children }) => (
  <div
    style={{
      height,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#999',
      fontSize: 13,
    }}
  >
    {children}
  </div>
);

// ---------------------------------------------------------------------------
// DayChart — one x position per day. Series can be bars or lines, on the left
// or right axis; bars can be stacked. Hover shows every value for that day.
// The most recent (partial) day is drawn faded / hollow / dashed.
// ---------------------------------------------------------------------------
const DayChart = ({
  rows,
  series,
  label,
  stacked = false,
  height = 220,
  leftFormat = fmtCompact,
  rightFormat = fmtCompact,
  integer = true,
  loading = false,
}) => {
  const [ref, width] = useElementWidth();
  const [hover, setHover] = useState(null);

  const hasData = rows.some(r => series.some(s => num(r[s.key]) > 0));
  const w = width || 600;
  const left = series.filter(s => s.axis !== 'right');
  const right = series.filter(s => s.axis === 'right');
  const m = { l: 44, r: right.length ? 44 : 12, t: 8, b: 24 };
  const iw = Math.max(w - m.l - m.r, 10);
  const ih = height - m.t - m.b;
  const n = rows.length;
  const band = iw / Math.max(n, 1);

  const maxOf = list => {
    let mx = 0;
    rows.forEach(r => {
      const bars = list.filter(s => s.type === 'bar');
      if (stacked) mx = Math.max(mx, bars.reduce((a, s) => a + num(r[s.key]), 0));
      else bars.forEach(s => { mx = Math.max(mx, num(r[s.key])); });
      list.filter(s => s.type !== 'bar').forEach(s => { mx = Math.max(mx, num(r[s.key])); });
    });
    return mx;
  };
  const L = niceScale(maxOf(left), 4, integer);
  const R = right.length ? niceScale(maxOf(right), 4, integer) : null;
  const y = (s, v) => m.t + ih - (v / (s.axis === 'right' ? R : L).max) * ih;
  const xC = i => m.l + band * (i + 0.5);

  // --- bars
  const bars = series.filter(s => s.type === 'bar');
  const groups = stacked ? 1 : Math.max(bars.length, 1);
  const barW = Math.min(band * 0.72, 34) / groups;
  const renderBars = () =>
    rows.map((r, i) => {
      let base = 0;
      return bars.map((s, k) => {
        const v = num(r[s.key]);
        if (!v) return null;
        const yTop = y(s, stacked ? base + v : v);
        const yBottom = y(s, stacked ? base : 0);
        if (stacked) base += v;
        const x = stacked ? xC(i) - barW / 2 : xC(i) - (barW * groups) / 2 + barW * k;
        return (
          <rect
            key={`${i}-${s.key}`}
            x={x}
            y={yTop}
            width={Math.max(barW - (groups > 1 ? 1 : 0), 1)}
            height={Math.max(yBottom - yTop, 0)}
            fill={s.color}
            stroke={stacked ? '#fff' : 'none'}
            strokeWidth={stacked ? 0.75 : 0}
            opacity={r.partial ? 0.35 : 1}
          />
        );
      });
    });

  // --- lines
  const showDots = n <= 35;
  const renderLine = s => {
    const pts = rows.map((r, i) => (r[s.key] == null ? null : [xC(i), y(s, num(r[s.key]))]));
    const solid = [];
    const dashed = [];
    let cur = [];
    pts.forEach((p, i) => {
      if (p == null) {
        if (cur.length) solid.push(cur);
        cur = [];
        return;
      }
      if (rows[i].partial) {
        if (cur.length) {
          dashed.push([cur[cur.length - 1], p]);
          solid.push(cur);
        }
        cur = [];
        return;
      }
      cur.push(p);
    });
    if (cur.length) solid.push(cur);
    const toPath = seg => seg.map((p, j) => `${j ? 'L' : 'M'}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' ');
    return (
      <g key={s.key}>
        {solid.map((seg, j) =>
          seg.length > 1 ? (
            <path key={`s${j}`} d={toPath(seg)} fill="none" stroke={s.color} strokeWidth={2} strokeLinejoin="round" />
          ) : null,
        )}
        {dashed.map((seg, j) => (
          <path
            key={`d${j}`}
            d={toPath(seg)}
            fill="none"
            stroke={s.color}
            strokeWidth={2}
            strokeDasharray="3 3"
            opacity={0.6}
          />
        ))}
        {pts.map((p, i) => {
          if (!p) return null;
          const isolated = pts[i - 1] == null && pts[i + 1] == null;
          if (!(rows[i].partial || showDots || isolated)) return null;
          return (
            <circle
              key={i}
              cx={p[0]}
              cy={p[1]}
              r={2.75}
              fill={rows[i].partial ? '#fff' : s.color}
              stroke={s.color}
              strokeWidth={rows[i].partial ? 1.5 : 0}
            />
          );
        })}
      </g>
    );
  };

  // --- x labels: anchored from the latest day so "today" is always labelled
  const maxLabels = Math.max(2, Math.floor(iw / 64));
  const every = Math.ceil(n / maxLabels);

  const onMove = e => {
    const box = e.currentTarget.getBoundingClientRect();
    const i = Math.floor((e.clientX - box.left) / band);
    setHover(Math.min(Math.max(i, 0), n - 1));
  };
  const hr = hover != null ? rows[hover] : null;
  const rightColor = right.length === 1 ? right[0].color : '#888';

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', fontSize: 12, color: '#555', marginBottom: 6 }}>
        {series.map(s => (
          <span key={s.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span
              style={{
                display: 'inline-block',
                width: 10,
                height: s.type === 'bar' ? 10 : 3,
                borderRadius: s.type === 'bar' ? 2 : 0,
                background: s.color,
              }}
            />
            {s.label}
          </span>
        ))}
      </div>

      {!hasData ? (
        <Placeholder height={height}>{loading ? 'Loading…' : 'No data in this window'}</Placeholder>
      ) : (
        <div style={{ position: 'relative', opacity: loading ? 0.5 : 1, transition: 'opacity 0.15s ease' }}>
          <svg
            width={w}
            height={height}
            role="img"
            aria-label={label}
            style={{ display: 'block', fontFamily: 'inherit' }}
          >
            {L.ticks.map((t, j) => {
              const yy = m.t + ih - (t / L.max) * ih;
              return (
                <g key={j}>
                  <line x1={m.l} x2={m.l + iw} y1={yy} y2={yy} stroke={j === 0 ? '#ced4da' : '#e9ecef'} />
                  <text x={m.l - 6} y={yy + 4} textAnchor="end" fontSize={11} fill="#888">
                    {leftFormat(t)}
                  </text>
                </g>
              );
            })}
            {R &&
              R.ticks.map((t, j) => {
                const yy = m.t + ih - (t / R.max) * ih;
                return (
                  <text key={j} x={m.l + iw + 6} y={yy + 4} textAnchor="start" fontSize={11} fill={rightColor}>
                    {rightFormat(t)}
                  </text>
                );
              })}

            {rows.map((r, i) =>
              (n - 1 - i) % every === 0 ? (
                <text key={r.day} x={xC(i)} y={height - 6} textAnchor="middle" fontSize={11} fill="#888">
                  {shortDay(r.day)}
                </text>
              ) : null,
            )}

            {hr && <rect x={m.l + band * hover} y={m.t} width={band} height={ih} fill="#000" opacity={0.05} />}
            {renderBars()}
            {series.filter(s => s.type === 'line').map(renderLine)}

            <rect
              x={m.l}
              y={m.t}
              width={iw}
              height={ih}
              fill="transparent"
              onMouseMove={onMove}
              onMouseLeave={() => setHover(null)}
            />
          </svg>

          {hr && (
            <div
              style={{
                position: 'absolute',
                top: 4,
                ...(xC(hover) > w / 2 ? { right: w - xC(hover) + 14 } : { left: xC(hover) + 14 }),
                pointerEvents: 'none',
                background: '#fff',
                border: '1px solid #dee2e6',
                borderRadius: 4,
                boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                padding: '8px 10px',
                fontSize: 12,
                zIndex: 2,
                whiteSpace: 'nowrap',
              }}
            >
              <div style={{ fontWeight: 600, color: '#333', marginBottom: 4 }}>
                {longDay(hr.day)}
                {hr.partial && <span style={{ fontWeight: 400, color: '#888' }}> · partial day</span>}
              </div>
              {series.map(s => (
                <div key={s.key} style={{ display: 'flex', justifyContent: 'space-between', gap: 18, color: '#555' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <span
                      style={{
                        display: 'inline-block',
                        width: 8,
                        height: s.type === 'bar' ? 8 : 3,
                        background: s.color,
                        borderRadius: s.type === 'bar' ? 2 : 0,
                      }}
                    />
                    {s.label}
                  </span>
                  <b style={{ color: '#333', fontVariantNumeric: 'tabular-nums' }}>
                    {(s.format || fmtInt)(hr[s.key])}
                  </b>
                </div>
              ))}
              {stacked && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 18,
                    marginTop: 4,
                    paddingTop: 4,
                    borderTop: '1px solid #eee',
                    color: '#555',
                  }}
                >
                  <span>Total</span>
                  <b style={{ color: '#333', fontVariantNumeric: 'tabular-nums' }}>
                    {fmtInt(series.reduce((a, s) => a + num(hr[s.key]), 0))}
                  </b>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// HBarList — ranked horizontal bars (position buckets, top coupons).
// ---------------------------------------------------------------------------
const HBarList = ({ items, color, format, labelWidth = 56, loading = false }) => {
  if (!items.length) {
    return <Placeholder height={120}>{loading ? 'Loading…' : 'No data in this window'}</Placeholder>;
  }
  const max = Math.max(...items.map(i => i.value), 0);
  return (
    <div style={{ opacity: loading ? 0.5 : 1, transition: 'opacity 0.15s ease' }}>
      {items.map(it => (
        <div key={it.label} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0', fontSize: 12 }}>
          <div style={{ width: labelWidth, flex: 'none', color: '#555', whiteSpace: 'nowrap' }}>{it.label}</div>
          <div style={{ flex: 1, height: 14, background: '#f1f3f5', borderRadius: 2 }}>
            <div
              style={{
                width: max ? `${(it.value / max) * 100}%` : 0,
                minWidth: it.value > 0 ? 2 : 0,
                height: '100%',
                background: color,
                borderRadius: 2,
              }}
            />
          </div>
          <div style={{ flex: 'none', width: 150, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
            <span style={{ fontWeight: 600, color: '#333' }}>{format(it.value)}</span>{' '}
            <span style={{ color: '#888' }}>{it.sub}</span>
          </div>
        </div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// SERIES DEFINITIONS
// ---------------------------------------------------------------------------
const TRAFFIC_SERIES = [
  { key: 'imp', label: 'Impressions', color: COLORS.lightBlue, type: 'bar', axis: 'left' },
  { key: 'clicks', label: 'Clicks (right axis)', color: COLORS.coral, type: 'line', axis: 'right' },
];
const CTR_SERIES = [
  { key: 'ctr', label: 'CTR', color: COLORS.blue, type: 'line', format: fmtPct },
  { key: 'reachCtr', label: 'Reach CTR', color: COLORS.teal, type: 'line', format: fmtPct },
];
const ACTION_SERIES = [
  { key: 'dir', label: 'Directions', color: COLORS.teal, type: 'bar' },
  { key: 'call', label: 'Calls', color: COLORS.purple, type: 'bar' },
  { key: 'fav', label: 'New favourites', color: COLORS.amber, type: 'bar' },
];
const USER_SERIES = [
  { key: 'users', label: 'Active users', color: COLORS.blue, type: 'line' },
  { key: 'sessions', label: 'Sessions', color: COLORS.teal, type: 'line' },
];
const TIME_SERIES = [
  { key: 'avgMin', label: 'Avg time in app per user', color: COLORS.purple, type: 'line', format: fmtMin },
];
const USER_ACTION_SERIES = [
  { key: 'affiliate', label: 'Affiliate clicks', color: COLORS.coral, type: 'bar' },
  { key: 'profile', label: 'Profile visits', color: COLORS.blue, type: 'bar' },
  { key: 'guestSessions', label: 'Guest sessions', color: COLORS.slate, type: 'line' },
];

// ---------------------------------------------------------------------------
// MAIN
// ---------------------------------------------------------------------------
const AnalyticsOverview = ({
  couponDays,
  userDays,
  dailyStats,
  positionBuckets,
  screenStats,
  ranking,
  userDailyStats,
  guestDailyStats,
  loading = {},
}) => {
  const todayISO = isoLocal(new Date());

  // --- per-day aggregates: coupon side (includes guests)
  const couponByDay = useMemo(() => {
    const map = {};
    unwrap(dailyStats).forEach(r => {
      const k = dayKey(r.day);
      const o = map[k] || (map[k] = { imp: 0, clicks: 0, reach: 0, uclicks: 0, dir: 0, call: 0, fav: 0 });
      o.imp += num(r.impressions_total);
      o.clicks += num(r.clicks_total);
      o.reach += num(r.reach_unique);
      o.uclicks += num(r.unique_clicks);
      o.dir += num(r.directions_clicks);
      o.call += num(r.call_clicks);
      o.fav += num(r.new_favourites);
    });
    return map;
  }, [dailyStats]);

  // --- per-day aggregates: signed-in users only (user_daily_stats skips guests)
  const userByDay = useMemo(() => {
    const map = {};
    unwrap(userDailyStats).forEach(r => {
      const k = dayKey(r.day);
      const o = map[k] || (map[k] = { users: 0, sessions: 0, fg: 0, affiliate: 0, profile: 0 });
      o.users += 1; // one row per user per day
      o.sessions += num(r.sessions);
      o.fg += num(r.foreground_time_ms);
      o.affiliate += num(r.affiliate_clicks);
      o.profile += num(r.profile_visits);
    });
    return map;
  }, [userDailyStats]);

    // --- per-day aggregates: guest sessions (guest_pseudo_user_id rows in
  // user_daily_stats — one row per anonymous session per day, not a person,
  // since guests have no identity that persists across days)
  const guestByDay = useMemo(() => {
    const map = {};
    unwrap(guestDailyStats).forEach(r => {
      const k = dayKey(r.day);
      const o = map[k] || (map[k] = { guestSessions: 0 });
      o.guestSessions += 1; // one row per guest session per day
    });
    return map;
  }, [guestDailyStats]);

  const couponRows = useMemo(
    () =>
      buildDays(couponDays, [couponByDay]).map(day => {
        const c = couponByDay[day] || {};
        return {
          day,
          partial: day === todayISO,
          imp: c.imp || 0,
          clicks: c.clicks || 0,
          ctr: c.imp ? c.clicks / c.imp : null,
          reachCtr: c.reach ? c.uclicks / c.reach : null,
          dir: c.dir || 0,
          call: c.call || 0,
          fav: c.fav || 0,
        };
      }),
    [couponDays, couponByDay, todayISO],
  );

  const userRows = useMemo(
    () =>
      buildDays(userDays, [userByDay, guestByDay]).map(day => {
        const u = userByDay[day] || {};
        const g = guestByDay[day] || {};
        return {
          day,
          partial: day === todayISO,
          users: u.users || 0,
          sessions: u.sessions || 0,
          guestSessions: g.guestSessions || 0,
          avgMin: u.users ? u.fg / u.users / 60000 : null,
          affiliate: u.affiliate || 0,
          profile: u.profile || 0,
        };
      }),
    [userDays, userByDay, guestByDay, todayISO],
  );

  // --- impressions by screen, per day (top 5 screens + "Other")
  const screenChart = useMemo(() => {
    const perDay = {};
    const totals = {};
    unwrap(screenStats).forEach(r => {
      const name = r.screen || '(none)';
      const k = dayKey(r.day);
      const v = num(r.impressions_total);
      const bucket = perDay[k] || (perDay[k] = {});
      bucket[name] = (bucket[name] || 0) + v;
      totals[name] = (totals[name] || 0) + v;
    });
    const names = Object.keys(totals).sort((a, b) => totals[b] - totals[a]);
    const top = names.slice(0, 5);
    const hasOther = names.length > 5;
    const series = top.map((name, i) => ({
      key: `s${i}`,
      label: name,
      color: SCREEN_COLORS[i],
      type: 'bar',
    }));
    if (hasOther) series.push({ key: 'other', label: 'Other', color: SCREEN_COLORS[5], type: 'bar' });
    const rows = buildDays(couponDays, [perDay]).map(day => {
      const d = perDay[day] || {};
      const o = { day, partial: day === todayISO };
      top.forEach((name, i) => { o[`s${i}`] = d[name] || 0; });
      if (hasOther) {
        o.other = names.slice(5).reduce((a, name) => a + (d[name] || 0), 0);
      }
      return o;
    });
    return { rows, series };
  }, [screenStats, couponDays, todayISO]);

  // --- CTR by feed-position bucket, whole window
  const bucketItems = useMemo(() => {
    const map = {};
    unwrap(positionBuckets).forEach(r => {
      const o = map[r.position_bucket] || (map[r.position_bucket] = { imp: 0, clicks: 0 });
      o.imp += num(r.impressions_total);
      o.clicks += num(r.clicks_total);
    });
    const order = b => {
      const i = BUCKET_ORDER.indexOf(b);
      return i === -1 ? 99 : i;
    };
    return Object.keys(map)
      .sort((a, b) => order(a) - order(b))
      .map(b => ({
        label: `#${b}`,
        value: map[b].imp ? map[b].clicks / map[b].imp : 0,
        sub: `${fmtCompact(map[b].imp)} imps`,
      }));
  }, [positionBuckets]);

  // --- top coupons by clicks, whole window
  const topCoupons = useMemo(
    () =>
      unwrap(ranking)
        .map(r => ({ id: r.coupon_id, imp: num(r.impressions), clicks: num(r.clicks) }))
        .sort((a, b) => b.clicks - a.clicks)
        .slice(0, 10)
        .map(c => ({
          label: `Coupon #${c.id}`,
          value: c.clicks,
          sub: `${fmtPct(c.imp ? c.clicks / c.imp : null)} CTR`,
        })),
    [ranking],
  );

  // --- KPI strip: completed days only (today's rollup is partial)
  const complete = couponRows.filter(r => !r.partial);
  const totals = complete.reduce(
    (a, r) => ({ imp: a.imp + r.imp, clicks: a.clicks + r.clicks, fav: a.fav + r.fav }),
    { imp: 0, clicks: 0, fav: 0 },
  );
  const completeUsers = userRows.filter(r => !r.partial);
  const userDaySum = completeUsers.reduce((a, r) => a + r.users, 0);
  const affiliateTotal = completeUsers.reduce((a, r) => a + r.affiliate, 0);
  const distinctUsers = useMemo(() => {
    const ids = new Set();
    unwrap(userDailyStats).forEach(r => {
      if (dayKey(r.day) !== todayISO) ids.add(r.user_id);
    });
    return ids.size;
  }, [userDailyStats, todayISO]);

  return (
    <div>
      <SectionLabel first>Overview</SectionLabel>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        <Kpi label="Impressions" value={fmtCompact(totals.imp)} sub={`${fmtInt(totals.imp)} total`} />
        <Kpi label="Clicks" value={fmtCompact(totals.clicks)} sub={`${fmtInt(totals.clicks)} total`} />
        <Kpi
          label="CTR"
          value={fmtPct(totals.imp ? totals.clicks / totals.imp : null)}
          sub="clicks ÷ impressions"
        />
        <Kpi
          label="Active users"
          value={fmtInt(distinctUsers)}
          sub={`${fmtInt(userDaySum / Math.max(completeUsers.length, 1))} per day · signed-in`}
        />
        <Kpi label="Affiliate clicks" value={fmtInt(affiliateTotal)} sub="signed-in users" />
        <Kpi label="New favourites" value={fmtInt(totals.fav)} />
      </div>
      <div style={{ fontSize: 11, color: '#888', marginTop: 8 }}>
        Totals cover completed days in the selected window. Today is left out and drawn faded or hollow in the
        charts: its rollup only refreshes when the nightly task runs.
      </div>

      <SectionLabel>Coupons — daily</SectionLabel>
      <Grid>
        <Card title="Impressions and clicks" sub="How much the feed is being seen, and how much of it gets clicked">
          <DayChart
            rows={couponRows}
            series={TRAFFIC_SERIES}
            label="Impressions and clicks per day"
            loading={loading.coupon}
          />
        </Card>
        <Card
          title="Click-through rate"
          sub="Reach CTR counts each user once per coupon per day, so heavy scrollers don't inflate it"
        >
          <DayChart
            rows={couponRows}
            series={CTR_SERIES}
            label="Click-through rate per day"
            leftFormat={fmtPctAxis}
            integer={false}
            loading={loading.coupon}
          />
        </Card>
        <Card title="What people do after clicking" sub="Directions, calls and new favourites, guests included">
          <DayChart
            rows={couponRows}
            series={ACTION_SERIES}
            label="Directions, calls and favourites per day"
            stacked
            loading={loading.coupon}
          />
        </Card>
      </Grid>

      <SectionLabel>Signed-in users — daily</SectionLabel>
      <Grid>
        <Card
          title="Active users and sessions"
          sub="Blue/teal are signed-in accounts; guest sessions can't be deduped across days, so they're a session count, not a user count"
        >
          <DayChart
            rows={userRows}
            series={USER_SERIES}
            label="Active users and sessions per day"
            loading={loading.user}
          />
        </Card>
        <Card title="Time in app" sub="Foreground time per active user">
          <DayChart
            rows={userRows}
            series={TIME_SERIES}
            label="Average minutes in app per active user per day"
            leftFormat={fmtMinAxis}
            integer={false}
            loading={loading.user}
          />
        </Card>
        <Card title="Affiliate clicks and profile visits" sub="Signed-in users only">
          <DayChart
            rows={userRows}
            series={USER_ACTION_SERIES}
            label="Affiliate clicks and profile visits per day"
            stacked
            loading={loading.user}
          />
        </Card>
      </Grid>

      <SectionLabel>Where impressions happen</SectionLabel>
      <Grid>
        <Card title="Impressions by screen" sub="Top five screens per day, the rest grouped as Other">
          <DayChart
            rows={screenChart.rows}
            series={screenChart.series}
            label="Impressions per day by screen"
            stacked
            loading={loading.screens}
          />
        </Card>
        <Card title="CTR by feed position" sub="Whole window. Does a higher slot actually earn more clicks?">
          <HBarList items={bucketItems} color={COLORS.blue} format={fmtPct} loading={loading.buckets} />
        </Card>
        <Card title="Top coupons by clicks" sub="Whole window">
          <HBarList
            items={topCoupons}
            color={COLORS.teal}
            format={fmtInt}
            labelWidth={92}
            loading={loading.ranking}
          />
        </Card>
      </Grid>
    </div>
  );
};

export default AnalyticsOverview;